import styled, {css, keyframes} from 'styled-components';

/**
 * Master column template of the panel. The collapsed bar cells live directly in this
 * grid; every expanded preset row joins the same tracks via subgrid, so the metric
 * columns stay pixel-aligned across all rows.
 * Columns: identity | supply×demand | price preview | price ladder | actions.
 */
export const PanelGrid = styled.div`
  display: grid;
  /* The ladder column may shrink below its content width on narrower desktops —
     the price boxes inside compress (and their font scales down) instead of the
     whole panel overflowing. */
  grid-template-columns:
    minmax(${({theme}) => theme.getSize(55)}, 1fr)
    max-content
    max-content
    minmax(${({theme}) => theme.getSize(90)}, max-content)
    max-content;
  column-gap: ${({theme}) => theme.getSize(6)};
  row-gap: ${({theme}) => theme.getSize(1)};
  align-items: center;
  min-width: 0;

  /* Notebook widths: tighter column gaps so the shrunken ladder still fits */
  @media ${({theme}) => theme.devices.notebook} {
    column-gap: ${({theme}) => theme.getSize(4)};
  }

  /* Below desktop the four metric columns don't fit — cells stack into
     one column with the actions pinned to the top-right corner. */
  @media ${({theme}) => theme.devices.mobile}, ${({theme}) => theme.devices.tablet} {
    grid-template-columns: minmax(0, 1fr) max-content;
    column-gap: ${({theme}) => theme.getSize(2)};
    row-gap: ${({theme}) => theme.getSize(2)};
  }
`;

/** Quick expand feedback: rows slide in from the collapsed bar as the panel opens. */
const rowSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/** Full-width child of {@link PanelGrid} (separator, create-preset row…). */
export const PanelGridFullWidth = styled.div`
  grid-column: 1 / -1;
  min-width: 0;
  /* fill-mode must stay 'backwards' — a forwards fill would pin the keyframe
     transform forever and override dnd-kit's inline drag transform */
  animation: ${rowSlideIn} 160ms ease backwards;
`;

/**
 * One preset row sharing the master grid tracks. A div with role="button" instead of
 * a real button — rows contain their own action buttons and buttons must not nest.
 */
export const PanelGridRow = styled.div<{$isActive: boolean; $isDragging?: boolean}>`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
  align-items: center;

  text-align: left;
  cursor: pointer;
  color: inherit;
  padding: ${({theme}) => `${theme.getSize(2)} 0`};
  border-radius: ${({theme}) => theme.radii.small};
  background-color: ${({theme, $isActive}) =>
    $isActive ? theme.colors.palettes.blue[10][100] : theme.colors.palettes.white[10][100]};
  opacity: ${({$isDragging}) => ($isDragging ? 0.5 : 1)};
  /* fill-mode 'backwards' (never forwards) + none while dragging — a filled or
     running animation would override dnd-kit's inline transform and the dragged
     row would not follow the cursor */
  ${({$isDragging}) =>
    $isDragging
      ? css`
          animation: none;
        `
      : css`
          animation: ${rowSlideIn} 160ms ease backwards;
        `}

  /* Hairline separator between consecutive rows */
  & + & {
    border-top: 1px solid ${({theme}) => theme.colors.palettes.neutral[30][100]};
  }

  &:hover,
  &:focus-visible {
    background-color: ${({theme}) => theme.colors.palettes.blue[10][100]};
    outline: none;
  }
`;
